import React, { memo, useCallback, useRef, useState } from 'react';
import { NodeSize } from '@/types';
import { useCanvasStore } from '@/stores/canvasStore';
import { startNodeResize, endNodeResize } from '@/components/flow/FlowEditor';

interface TextNodeResizeHandleProps {
  minWidth?: number;
  minHeight?: number;
  onResize: (size: NodeSize) => void;
}

const TextNodeResizeHandleInner: React.FC<TextNodeResizeHandleProps> = ({
  minWidth = 480,
  minHeight = 320,
  onResize,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const startRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  // 方案 6：改为非响应式读取，避免 canvasStore.zoom 更新时所有文本节点重渲染
  // zoom 仅在 resize 时使用，交互开始时读取最新值即可
  const zoomRef = useRef(useCanvasStore.getState().zoom);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // 向上查找最近的节点卡片容器
    const nodeEl = e.currentTarget.closest('[data-node-card]');
    if (!nodeEl) return;

    // mousedown 时读取最新 zoom（避免使用过时值）
    const currentZoom = useCanvasStore.getState().zoom;
    zoomRef.current = currentZoom;
    const rect = nodeEl.getBoundingClientRect();
    // rect 是缩放后的视觉尺寸，除以 zoom 得到逻辑尺寸
    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      w: rect.width / currentZoom,
      h: rect.height / currentZoom,
    };

    setIsResizing(true);
    startNodeResize(); // 通知 FlowEditor 跳过全量重建 localNodes

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      if (!startRef.current) return;

      // rAF 节流，避免每次 mousemove 都触发重渲染
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        if (!startRef.current) { rafRef.current = null; return; }
        const { x, y, w, h } = startRef.current;
        const currentZoom = zoomRef.current;
        const deltaX = (moveEvent.clientX - x) / currentZoom;
        const deltaY = (moveEvent.clientY - y) / currentZoom;

        const newWidth = Math.max(minWidth, Math.round(w + deltaX));
        const newHeight = Math.max(minHeight, Math.round(h + deltaY));

        onResize({ width: newWidth, height: newHeight });
        rafRef.current = null;
      });
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      upEvent.stopPropagation();
      // 确保最后一次 resize 生效
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setIsResizing(false);
      endNodeResize(); // 通知 FlowEditor 恢复全量重建
      startRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [minWidth, minHeight, onResize]);

  return (
    <div
      className={`nodrag absolute right-2 bottom-2 w-8 h-8 cursor-nwse-resize transition-opacity z-10 opacity-0 group-hover:opacity-50 ${
        isResizing ? '!opacity-100' : 'hover:opacity-100'
      }`}
      style={{
        background: `repeating-linear-gradient(
          135deg,
          rgba(255,255,255,0.4) 0px,
          rgba(255,255,255,0.4) 3px,
          transparent 3px,
          transparent 8px
        )`,
        clipPath: 'polygon(100% 0%, 100% 100%, 0% 100%)',
      }}
      onMouseDown={handleMouseDown}
    />
  );
};

export const TextNodeResizeHandle = memo(TextNodeResizeHandleInner);
